/**
 * WebNovelViewer.js
 * PURE UI Component for rendering text content.
 * Decoupled from Page logic and Settings.
 * 
 * Responsibilities:
 * 1. Render Title, Content, Note, Navigation
 * 2. Provide slots for Footer (Rating, Comments)
 * 3. Respond to Font Size updates via API
 */
import { MirinaeSanitizer } from "../../../utils/MirinaeSanitizer.js";
import "../../../styles/mirinae-editor.css"; // Shared Typography (Quote Styling)

export const WebNovelViewer = async (container, {
    content,
    title,
    note,
    onNext,
    onPrev,
    hasPrev,
    hasNext,
    isbn,
    uci,
    initialFontSize = 18
}) => {

    // Sanitize & Prepare Content
    const safeContent = MirinaeSanitizer.isHtml(content)
        ? MirinaeSanitizer.sanitize(content)
        : MirinaeSanitizer.convertLegacyToHtml(content);

    // 1. Render Template
    // Note: We intentionally do NOT set theme classes here. The Parent (Page) controls the theme context.
    container.innerHTML = `
        <div id="nv-app-root" class="relative w-full max-w-lg mx-auto transition-colors duration-300">
            
            <!-- READER BODY -->
            <div id="reader-scroll" class="w-full px-6 pt-24 pb-32 relative bg-transparent">
                <h3 class="text-xl font-bold mb-8 text-indigo-500 opacity-90">${title}</h3>
                
                <!-- Main Content -->
                <div id="reader-body" class="reader-content text-base break-keep leading-loose" 
                     style="font-size: ${initialFontSize}px;">${safeContent}</div>
                
                ${note ? `
                <div class="mt-12 p-5 rounded-xl ui-bg border border-white/5 bg-white/5 backdrop-blur-sm">
                    <div class="text-xs font-bold opacity-50 mb-2 flex items-center gap-1">
                        <span class="material-symbols-outlined text-sm">edit_note</span> 
                        작가의 말
                    </div>
                    <p class="text-sm opacity-80 whitespace-pre-wrap leading-relaxed">${note}</p>
                </div>` : ''}

                <!-- Navigation consolidated into ViewerFooter -->


                <!-- [FIX] Rating Slot (Restored) -->
                <div id="rating-slot" class="mt-12 flex justify-center min-h-[60px]"></div>

                <!-- Footer Slot (Comments) -->
                <div id="viewer-footer" class="mt-8 border-t border-white/10 pt-8"></div>
                
                <!-- Metadata -->
                <div class="text-center py-10 text-xs opacity-30 flex flex-col items-center gap-1 font-mono">
                    <span class="tracking-widest">MIRINAE VIEWER v0.7.0</span>
                    ${isbn ? `<span>ISBN: ${isbn}</span>` : ''}
                    ${uci ? `<span>UCI: ${uci}</span>` : ''}
                </div>
        </div>
    `;

    // 2. Element Refs
    const body = container.querySelector('#reader-body');
    const btnPrev = container.querySelector('#wn-btn-prev');
    const btnNext = container.querySelector('#wn-btn-next');

    // 3. Bind Events
    // 3. Bind Events (Navigation handled by ViewerFooter mainly, but keyboard shortcuts could go here if needed)
    // Legacy button listeners removed.


    // 4. Expose API for Parent/Controller
    const api = {
        setFontSize: (size) => {
            if (body) body.style.fontSize = `${size}px`;
        },
        updateContent: (newContent, newTitle, newNote) => {
            if (newTitle) container.querySelector('h3').textContent = newTitle;
            if (newContent) {
                const safe = MirinaeSanitizer.isHtml(newContent) ? MirinaeSanitizer.sanitize(newContent) : MirinaeSanitizer.convertLegacyToHtml(newContent);
                body.innerHTML = safe;
            }
            if (typeof newNote !== 'undefined') { // Allow clearing note
                // Note logic is complex because the element might not exist.
                // For MVP, we will just re-render the note logic if simplified, or skip note updates for now.
                // Actually, let's keep it simple: Just update content and title.
                // Re-implementing note DOM manipulation is overkill for this step.
            }
        },
        getElement: () => container.querySelector('#nv-app-root')
    };

    // Return the API so the caller can control this component
    return api;
};
